import classNames from 'classnames'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import {
    EVENT_CREATOR_EVENTS,
    createEventCreatorAnalyticsContext,
    createEventCreatorAnalyticsId,
} from 'common/services/EventCreatorAnalytics'
import Mixpanel from 'common/services/Mixpanel'
import backIcon from 'svg/arrow-left-blue-bold.svg'

import { checkIcon, EVENT_TILE_ILLUSTRATIONS, heroIllustration } from './assets'
import { EVENT_CREATOR_EXPERIMENT_VARIANT, eventCategories, getSelectedTiles, getTilesForCategory } from './config'
import { SETUP_RECOMMENDATION_PREVIEW_LIMIT } from './recommendationConfig/setup'
import { loadEventTemplateRecommendations } from './recommendations'
import { toggleTileSelection } from './selection'
import { recommendEventSetup } from './setupRecommendations'

import styles from './EventCreator.module.scss'

const INITIAL_CATEGORY_ID = eventCategories[0].id
const POPULAR_TILE_LIMIT = 6
const MIN_RECOMMENDATION_LOADING_TIME = 1000
// TODO: Restore the free-form event description after recommendations are backed by LLM-based intent analysis.
const EVENT_DESCRIPTION = ''

const wait = duration => new Promise(resolve => setTimeout(resolve, duration))

const ANALYTICS_CATEGORY_PROPERTIES = {
    event_type: 'selectedEventTypeIds',
    goal: 'selectedGoalIds',
    mechanic: 'selectedGameTypeIds',
    participation: 'selectedParticipationIds',
    device: 'selectedDeviceIds',
    leads: 'selectedLeadIds',
    rewards: 'selectedPrizeIds',
    scale: 'selectedScaleIds',
    integration: 'selectedIntegrationIds',
}

const TileIllustration = ({ tile, frameClassName }) => {
    const illustration = EVENT_TILE_ILLUSTRATIONS[tile.id]
    if (!illustration) return null

    return (
        <span className={frameClassName} aria-hidden="true">
            <img
                className={classNames(styles.illustrationImage, {
                    [styles.tradeShowIllustration]: tile.id === 'venue_trade_show',
                })}
                src={illustration}
                alt=""
            />
        </span>
    )
}

TileIllustration.propTypes = {
    tile: PropTypes.shape({
        id: PropTypes.string.isRequired,
    }).isRequired,
    frameClassName: PropTypes.string.isRequired,
}

const createAnalyticsPayload = ({
    categoryId,
    tile,
    selectedTileIds,
    description,
    isAuthorized,
    experimentVariant,
    analyticsContext,
    submission,
    editCount,
    recommendationBatchNumber,
    displayMode,
}) => {
    const selectedTiles = getSelectedTiles(selectedTileIds)
    const selectedIdsByCategory = Object.entries(ANALYTICS_CATEGORY_PROPERTIES).reduce(
        (payload, [selectedCategoryId, propertyName]) => {
            payload[propertyName] = selectedTiles
                .filter(selectedTile => selectedTile.categoryId === selectedCategoryId)
                .map(selectedTile => selectedTile.id)
            return payload
        },
        {},
    )

    return {
        ...analyticsContext,
        categoryId,
        ...(tile && {
            tileId: tile.id,
            tileCategoryId: tile.categoryId,
            analyticsKey: tile.analyticsKey,
        }),
        selectedTileIds,
        selectedCount: selectedTileIds.length,
        ...selectedIdsByCategory,
        hasDescription: Boolean(description.trim()),
        isAuthorized,
        experimentVariant,
        editCount,
        displayMode,
        ...(recommendationBatchNumber > 0 && { recommendationBatchNumber }),
        ...(submission && {
            submissionId: submission.id,
            submissionNumber: submission.number,
            isResubmission: submission.number > 1,
        }),
    }
}

const EventCreator = ({
    initiallyCollapsed,
    isAuthorized,
    experimentVariant,
    onboardingInterests,
    onStartTemplate,
    surface,
}) => {
    const { i18n, t } = useTranslation()
    const [activeCategoryId, setActiveCategoryId] = useState(INITIAL_CATEGORY_ID)
    const [selectedTileIds, setSelectedTileIds] = useState([])
    const [recommendations, setRecommendations] = useState([])
    const [recommendationStatus, setRecommendationStatus] = useState('idle')
    const [activeStep, setActiveStep] = useState('setup')
    const [isShowingAllSetupRecommendations, setIsShowingAllSetupRecommendations] = useState(false)
    const [hasMoreRecommendations, setHasMoreRecommendations] = useState(true)
    const [isCollapsed, setIsCollapsed] = useState(initiallyCollapsed)
    const [hasSelectionError, setHasSelectionError] = useState(false)
    const recommendationRequestId = useRef(0)
    const recommendationBatchNumber = useRef(0)
    const displayedRecommendationBatchNumber = useRef(0)
    const shownTemplateIds = useRef(new Set())
    const analyticsContext = useRef(null)
    const activeSubmission = useRef(null)
    const editCount = useRef(0)
    const hasStarted = useRef(false)
    const submissionCount = useRef(0)
    const setupHeadingRef = useRef(null)
    const resultsHeadingRef = useRef(null)
    const previousStep = useRef(activeStep)
    const isLoadingRecommendations = recommendationStatus === 'loading'

    if (!analyticsContext.current) {
        analyticsContext.current = createEventCreatorAnalyticsContext({
            language: i18n.language,
            surface,
            initialDisplayMode: initiallyCollapsed ? 'collapsed' : 'full',
            onboardingInterestIds: onboardingInterests.map(interest => interest.id).filter(Boolean),
            onboardingInterestNames: onboardingInterests.map(interest => interest.name).filter(Boolean),
        })
    }

    const getAnalyticsPayload = ({
        categoryId = activeCategoryId,
        tile,
        nextSelectedTileIds = selectedTileIds,
        displayMode = isCollapsed ? 'collapsed' : 'full',
        analyticsRecommendationBatchNumber = displayedRecommendationBatchNumber.current,
    } = {}) =>
        createAnalyticsPayload({
            categoryId,
            tile,
            selectedTileIds: nextSelectedTileIds,
            description: EVENT_DESCRIPTION,
            isAuthorized,
            experimentVariant,
            analyticsContext: analyticsContext.current,
            submission: activeSubmission.current,
            editCount: editCount.current,
            recommendationBatchNumber: analyticsRecommendationBatchNumber,
            displayMode,
        })

    const categoryTiles = useMemo(() => getTilesForCategory(activeCategoryId), [activeCategoryId])
    const selectedTiles = useMemo(() => getSelectedTiles(selectedTileIds), [selectedTileIds])
    const setupRecommendations = useMemo(
        () => recommendEventSetup({ selectedTileIds, description: EVENT_DESCRIPTION }),
        [selectedTileIds],
    )
    const visibleSetupRecommendations = isShowingAllSetupRecommendations
        ? setupRecommendations
        : setupRecommendations.slice(0, SETUP_RECOMMENDATION_PREVIEW_LIMIT)
    const visibleTiles = activeCategoryId === 'popular' ? categoryTiles.slice(0, POPULAR_TILE_LIMIT) : categoryTiles

    useEffect(() => {
        Mixpanel.track(
            EVENT_CREATOR_EVENTS.VIEWED,
            createAnalyticsPayload({
                categoryId: INITIAL_CATEGORY_ID,
                selectedTileIds: [],
                description: EVENT_DESCRIPTION,
                isAuthorized,
                experimentVariant,
                analyticsContext: analyticsContext.current,
                editCount: 0,
                displayMode: initiallyCollapsed ? 'collapsed' : 'full',
            }),
        )
    }, [experimentVariant, initiallyCollapsed, isAuthorized])

    useEffect(
        () => () => {
            recommendationRequestId.current += 1
        },
        [],
    )

    useEffect(() => {
        if (previousStep.current === activeStep) return
        previousStep.current = activeStep

        const heading = activeStep === 'results' ? resultsHeadingRef.current : setupHeadingRef.current
        if (typeof heading?.scrollIntoView === 'function') {
            heading.scrollIntoView({ block: 'start' })
        }
        heading?.focus({ preventScroll: true })
    }, [activeStep])

    const handleCategoryChange = categoryId => {
        if (categoryId === activeCategoryId) return

        setActiveCategoryId(categoryId)
        Mixpanel.track(EVENT_CREATOR_EVENTS.CATEGORY_OPENED, getAnalyticsPayload({ categoryId }))
    }

    const handleTileToggle = tile => {
        const isSelected = selectedTileIds.includes(tile.id)
        const nextSelectedTileIds = toggleTileSelection(selectedTileIds, tile)
        setSelectedTileIds(nextSelectedTileIds)
        if (nextSelectedTileIds.length > 0) setHasSelectionError(false)

        if (!isSelected && !hasStarted.current) {
            hasStarted.current = true
            Mixpanel.track(EVENT_CREATOR_EVENTS.STARTED, getAnalyticsPayload({ tile, nextSelectedTileIds }))
        }

        Mixpanel.track(
            isSelected ? EVENT_CREATOR_EVENTS.TILE_DESELECTED : EVENT_CREATOR_EVENTS.TILE_SELECTED,
            getAnalyticsPayload({ tile, nextSelectedTileIds }),
        )
    }

    const requestTemplateRecommendations = async ({ excludedTemplateIds = [], requestId, requestBatchNumber }) => {
        const isAlternativeBatch = requestBatchNumber > 1
        try {
            const [nextRecommendations] = await Promise.all([
                loadEventTemplateRecommendations({
                    selectedTileIds,
                    description: EVENT_DESCRIPTION,
                    preferredLanguage: i18n.language,
                    excludedTemplateIds,
                }),
                wait(MIN_RECOMMENDATION_LOADING_TIME),
            ])

            if (recommendationRequestId.current !== requestId) return

            if (nextRecommendations.length > 0) {
                setRecommendations(nextRecommendations)
                displayedRecommendationBatchNumber.current = requestBatchNumber
                nextRecommendations.forEach(recommendation => shownTemplateIds.current.add(recommendation.template.id))
            } else if (isAlternativeBatch) {
                setHasMoreRecommendations(false)
            }
            setRecommendationStatus('success')
            Mixpanel.track(EVENT_CREATOR_EVENTS.RECOMMENDATIONS_VIEWED, {
                ...getAnalyticsPayload({
                    analyticsRecommendationBatchNumber: requestBatchNumber,
                }),
                templateIds: nextRecommendations.map(recommendation => recommendation.template.id),
                templateLanguages: nextRecommendations.map(recommendation => recommendation.template.language),
                resultCount: nextRecommendations.length,
                freeTemplateCount: nextRecommendations.filter(
                    recommendation => recommendation.template.isPremium === false,
                ).length,
                setupFeatureIds: setupRecommendations.map(recommendation => recommendation.id),
                isAlternativeBatch,
                excludedTemplateIds,
            })
        } catch (error) {
            if (recommendationRequestId.current !== requestId) return

            setRecommendationStatus('error')
            Mixpanel.track(EVENT_CREATOR_EVENTS.RECOMMENDATIONS_FAILED, {
                ...getAnalyticsPayload({
                    analyticsRecommendationBatchNumber: requestBatchNumber,
                }),
                errorMessage: error instanceof Error ? error.message : String(error),
                isAlternativeBatch,
                excludedTemplateIds,
            })
        }
    }

    const handleSubmit = event => {
        event.preventDefault()
        if (isLoadingRecommendations) return
        if (selectedTileIds.length === 0) {
            setHasSelectionError(true)
            Mixpanel.track(EVENT_CREATOR_EVENTS.SUBMISSION_BLOCKED, {
                ...getAnalyticsPayload(),
                reason: 'no_selected_options',
            })
            return
        }

        const requestId = recommendationRequestId.current + 1
        recommendationRequestId.current = requestId
        recommendationBatchNumber.current = 1
        displayedRecommendationBatchNumber.current = 0
        shownTemplateIds.current = new Set()
        submissionCount.current += 1
        activeSubmission.current = {
            id: createEventCreatorAnalyticsId(),
            number: submissionCount.current,
        }
        setRecommendations([])
        setRecommendationStatus('loading')
        setIsShowingAllSetupRecommendations(false)
        setHasMoreRecommendations(true)
        setActiveStep('results')

        Mixpanel.track(
            EVENT_CREATOR_EVENTS.SUBMITTED,
            getAnalyticsPayload({
                analyticsRecommendationBatchNumber: recommendationBatchNumber.current,
            }),
        )
        requestTemplateRecommendations({
            requestId,
            requestBatchNumber: recommendationBatchNumber.current,
        })
    }

    const handleAlternativeRecommendations = () => {
        if (isLoadingRecommendations || !hasMoreRecommendations) return

        const requestId = recommendationRequestId.current + 1
        recommendationRequestId.current = requestId
        recommendationBatchNumber.current += 1
        const excludedTemplateIds = Array.from(shownTemplateIds.current)

        setRecommendationStatus('loading')
        Mixpanel.track(EVENT_CREATOR_EVENTS.ALTERNATIVE_RECOMMENDATIONS_REQUESTED, {
            ...getAnalyticsPayload({
                analyticsRecommendationBatchNumber: recommendationBatchNumber.current,
            }),
            currentTemplateIds: recommendations.map(recommendation => recommendation.template.id),
            excludedTemplateIds,
        })
        requestTemplateRecommendations({
            excludedTemplateIds,
            requestId,
            requestBatchNumber: recommendationBatchNumber.current,
        })
    }

    const handleEditEvent = () => {
        editCount.current += 1
        Mixpanel.track(EVENT_CREATOR_EVENTS.EDIT_STARTED, getAnalyticsPayload())
        recommendationRequestId.current += 1
        setRecommendations([])
        setRecommendationStatus('idle')
        setIsShowingAllSetupRecommendations(false)
        setHasMoreRecommendations(true)
        setActiveStep('setup')
    }

    const handleSetupHelpOpen = (setupRecommendation, recommendationIndex) => {
        Mixpanel.track(EVENT_CREATOR_EVENTS.SETUP_HELP_OPENED, {
            ...getAnalyticsPayload(),
            setupFeatureId: setupRecommendation.id,
            recommendationIndex,
        })
    }

    const handleTemplateOpen = (recommendation, recommendationIndex) => {
        Mixpanel.track(EVENT_CREATOR_EVENTS.TEMPLATE_OPENED, {
            ...getAnalyticsPayload(),
            templateId: recommendation.template.id,
            templateLanguage: recommendation.template.language,
            isPremium: recommendation.template.isPremium,
            recommendationIndex,
        })
    }

    const handleTemplateStart = (recommendation, recommendationIndex) => {
        const { template } = recommendation
        Mixpanel.track(EVENT_CREATOR_EVENTS.TEMPLATE_STARTED, {
            ...getAnalyticsPayload(),
            templateId: template.id,
            templateLanguage: template.language,
            isPremium: template.isPremium,
            recommendationIndex,
        })
        onStartTemplate(Number(template.id), {
            creatorSessionId: analyticsContext.current.creatorSessionId,
            creatorInstanceId: analyticsContext.current.creatorInstanceId,
            submissionId: activeSubmission.current?.id,
            submissionNumber: activeSubmission.current?.number,
            recommendationIndex,
            recommendationBatchNumber: displayedRecommendationBatchNumber.current,
            experimentVariant,
            surface,
            initialDisplayMode: analyticsContext.current.initialDisplayMode,
            onboardingInterestIds: analyticsContext.current.onboardingInterestIds,
            onboardingInterestNames: analyticsContext.current.onboardingInterestNames,
        })
    }

    const handleSetupVisibilityToggle = () => {
        const isExpanded = !isShowingAllSetupRecommendations
        setIsShowingAllSetupRecommendations(isExpanded)
        Mixpanel.track(EVENT_CREATOR_EVENTS.SETUP_VISIBILITY_CHANGED, {
            ...getAnalyticsPayload(),
            isExpanded,
            visibleSetupFeatureCount: isExpanded
                ? setupRecommendations.length
                : Math.min(SETUP_RECOMMENDATION_PREVIEW_LIMIT, setupRecommendations.length),
        })
    }

    const handleGalleryOpen = () => {
        Mixpanel.track(EVENT_CREATOR_EVENTS.GALLERY_OPENED, getAnalyticsPayload())
    }

    const handleExpand = () => {
        setIsCollapsed(false)
        Mixpanel.track(EVENT_CREATOR_EVENTS.EXPANDED, {
            ...getAnalyticsPayload({ displayMode: 'full' }),
            activeStep,
        })
    }

    const handleCollapse = () => {
        setIsCollapsed(true)
        Mixpanel.track(EVENT_CREATOR_EVENTS.COLLAPSED, {
            ...getAnalyticsPayload({ displayMode: 'collapsed' }),
            activeStep,
        })
    }

    if (isCollapsed) {
        return (
            <section
                className={classNames(styles.panel, styles.collapsedPanel)}
                data-test="event-creator"
                data-display-mode="collapsed"
            >
                <header className={classNames(styles.header, styles.collapsedHeader)}>
                    <div className={styles.heroContent}>
                        <h2 className={classNames(styles.title, styles.collapsedTitle)}>{t('Create an Event')}</h2>
                        <p className={styles.subtitle}>
                            {t("Tell us what you're planning, and we'll recommend the right template and setup.")}
                        </p>
                        <button
                            type="button"
                            className={classNames(styles.submit, styles.expandButton)}
                            data-test="event-creator-expand"
                            onClick={handleExpand}
                        >
                            {t('Open Event Creator')}
                        </button>
                    </div>
                    <div className={styles.heroArtwork} aria-hidden="true">
                        <img src={heroIllustration} alt="" />
                    </div>
                </header>
            </section>
        )
    }

    return (
        <section className={styles.panel} data-test="event-creator" data-display-mode="full">
            {activeStep === 'setup' && (
                <>
                    <header className={styles.header}>
                        <div className={styles.heroContent}>
                            <h2 ref={setupHeadingRef} className={styles.title} tabIndex="-1">
                                {t('Create an Event')}
                            </h2>
                            <p className={styles.subtitle}>
                                {t("Tell us what you're planning, and we'll recommend the right template and setup.")}
                            </p>
                        </div>
                        <div className={styles.heroArtwork} aria-hidden="true">
                            <img src={heroIllustration} alt="" />
                        </div>
                    </header>

                    {selectedTiles.length > 0 && (
                        <div className={styles.selected} data-test="event-creator-selected">
                            <div className={styles.selectedTags}>
                                {selectedTiles.map(tile => {
                                    return (
                                        <button
                                            key={tile.id}
                                            type="button"
                                            className={styles.selectedTag}
                                            data-test={`event-creator-selected-${tile.id}`}
                                            aria-label={t('Remove {{label}}', { label: tile.label })}
                                            onClick={() => handleTileToggle(tile)}
                                        >
                                            <TileIllustration tile={tile} frameClassName={styles.tagIllustration} />
                                            <span>{t(tile.shortLabel || tile.label)}</span>
                                        </button>
                                    )
                                })}
                            </div>
                        </div>
                    )}

                    <div className={styles.categoryBar}>
                        <div className={styles.categories} role="tablist" aria-label={t('Event categories')}>
                            {eventCategories
                                .slice()
                                .sort((firstCategory, secondCategory) => firstCategory.order - secondCategory.order)
                                .map(category => (
                                    <button
                                        key={category.id}
                                        type="button"
                                        id={`event-creator-tab-${category.id}`}
                                        className={classNames(styles.category, {
                                            [styles.categoryActive]: activeCategoryId === category.id,
                                        })}
                                        role="tab"
                                        aria-selected={activeCategoryId === category.id}
                                        aria-controls="event-creator-tiles"
                                        onClick={() => handleCategoryChange(category.id)}
                                    >
                                        {t(category.label)}
                                    </button>
                                ))}
                        </div>
                    </div>

                    <div
                        id="event-creator-tiles"
                        className={styles.tiles}
                        role="tabpanel"
                        aria-labelledby={`event-creator-tab-${activeCategoryId}`}
                    >
                        {visibleTiles.map(tile => {
                            const isSelected = selectedTileIds.includes(tile.id)
                            return (
                                <button
                                    key={tile.id}
                                    type="button"
                                    className={classNames(styles.tile, { [styles.tileSelected]: isSelected })}
                                    aria-pressed={isSelected}
                                    data-test={`event-creator-tile-${tile.id}`}
                                    onClick={() => handleTileToggle(tile)}
                                >
                                    <TileIllustration tile={tile} frameClassName={styles.tileIllustration} />
                                    <span className={styles.tileLabel}>{t(tile.label)}</span>
                                    {tile.description && (
                                        <span className={styles.tileDescription}>{t(tile.description)}</span>
                                    )}
                                    {isSelected && (
                                        <img className={styles.tileCheck} src={checkIcon} alt="" aria-hidden="true" />
                                    )}
                                </button>
                            )
                        })}
                    </div>

                    <form className={styles.form} onSubmit={handleSubmit}>
                        <div className={styles.actions}>
                            <button
                                type="submit"
                                className={styles.submit}
                                data-test="event-creator-submit"
                                disabled={isLoadingRecommendations}
                                aria-describedby={hasSelectionError ? 'event-creator-selection-error' : undefined}
                            >
                                {t('Get recommendations')}
                            </button>
                            <span className={styles.actionHint}>
                                {t("We'll suggest a template, settings, and the right plan.")}
                            </span>
                        </div>
                        {hasSelectionError && (
                            <p
                                id="event-creator-selection-error"
                                className={styles.selectionError}
                                role="alert"
                                data-test="event-creator-selection-error"
                            >
                                {t('Choose at least one option to get recommendations.')}
                            </p>
                        )}
                    </form>
                </>
            )}

            {activeStep === 'results' && (
                <>
                    <div className={styles.resultsOverview}>
                        <header className={styles.resultsHeader} data-test="event-creator-results-step">
                            <button
                                type="button"
                                className={styles.backButton}
                                data-test="event-creator-edit-event"
                                aria-label={t('Back')}
                                title={t('Back')}
                                onClick={handleEditEvent}
                            >
                                <img src={backIcon} alt="" aria-hidden="true" />
                            </button>
                            <div className={styles.resultsHeaderContent}>
                                <h2 ref={resultsHeadingRef} className={styles.resultsHeading} tabIndex="-1">
                                    {t('Recommended for your event')}
                                </h2>
                                {selectedTiles.length > 0 ? (
                                    <div
                                        className={styles.resultsConditions}
                                        data-test="event-creator-results-conditions"
                                    >
                                        {selectedTiles.map(tile => {
                                            return (
                                                <span
                                                    key={tile.id}
                                                    className={styles.resultCondition}
                                                    data-test={`event-creator-result-condition-${tile.id}`}
                                                >
                                                    <TileIllustration
                                                        tile={tile}
                                                        frameClassName={styles.tagIllustration}
                                                    />
                                                    <span>{t(tile.shortLabel || tile.label)}</span>
                                                </span>
                                            )
                                        })}
                                    </div>
                                ) : (
                                    <p className={styles.eventBrief}>{t('Custom event')}</p>
                                )}
                            </div>
                        </header>

                        {isLoadingRecommendations && recommendations.length === 0 && (
                            <section
                                className={styles.recommendationsLoading}
                                role="status"
                                aria-live="polite"
                                aria-label={t('Finding templates')}
                                data-test="event-creator-recommendations-loading"
                            >
                                <span className={styles.recommendationsSpinner} aria-hidden="true" />
                                <span>{t('Finding the right templates for your event...')}</span>
                            </section>
                        )}

                        {recommendationStatus === 'error' && recommendations.length === 0 && (
                            <section
                                className={styles.recommendationsError}
                                role="alert"
                                data-test="event-creator-recommendations-error"
                            >
                                {t('Something went wrong. Please try again later')}
                            </section>
                        )}

                        {recommendations.length > 0 && setupRecommendations.length > 0 && (
                            <section
                                className={styles.setupRecommendations}
                                data-test="event-creator-setup-recommendations"
                            >
                                <div className={styles.setupRecommendationsHeader}>
                                    <div>
                                        <h3 className={styles.setupRecommendationsTitle}>{t('Recommended setup')}</h3>
                                        <p className={styles.setupRecommendationsSubtitle}>
                                            {t('Review these settings after choosing a template.')}
                                        </p>
                                    </div>
                                    {setupRecommendations.length > SETUP_RECOMMENDATION_PREVIEW_LIMIT && (
                                        <button
                                            type="button"
                                            className={styles.setupShowMore}
                                            onClick={handleSetupVisibilityToggle}
                                        >
                                            {t(isShowingAllSetupRecommendations ? 'Show less' : 'Show all')}
                                        </button>
                                    )}
                                </div>

                                <div className={styles.setupGrid}>
                                    {visibleSetupRecommendations.map((setupRecommendation, recommendationIndex) => {
                                        const learnMoreLabel = t('Learn more about {{feature}}', {
                                            feature: t(setupRecommendation.title),
                                        })

                                        return (
                                            <article
                                                key={setupRecommendation.id}
                                                className={styles.setupCard}
                                                data-test={`event-creator-setup-${setupRecommendation.id}`}
                                            >
                                                <span className={styles.setupIcon} aria-hidden="true">
                                                    {setupRecommendation.icon}
                                                </span>
                                                <div className={styles.setupCardContent}>
                                                    <h4 className={styles.setupCardTitle}>
                                                        {t(setupRecommendation.title)}
                                                    </h4>
                                                    <p className={styles.setupCardDescription}>
                                                        {t(setupRecommendation.description)}
                                                    </p>
                                                </div>
                                                {setupRecommendation.helpUrl && (
                                                    <a
                                                        className={styles.setupHelp}
                                                        href={setupRecommendation.helpUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        aria-label={learnMoreLabel}
                                                        title={learnMoreLabel}
                                                        onClick={() =>
                                                            handleSetupHelpOpen(
                                                                setupRecommendation,
                                                                recommendationIndex,
                                                            )
                                                        }
                                                    >
                                                        ?
                                                    </a>
                                                )}
                                            </article>
                                        )
                                    })}
                                </div>
                            </section>
                        )}
                    </div>

                    {recommendations.length > 0 && (
                        <section
                            className={styles.recommendations}
                            aria-live="polite"
                            data-test="event-creator-recommendations"
                        >
                            <div className={styles.recommendationsHeader}>
                                <h3 className={styles.recommendationsTitle}>{t('Templates')}</h3>
                                <p className={styles.recommendationsSubtitle}>
                                    <Trans
                                        i18nKey="We recommend these templates, but you can choose any template from our <galleryLink>full gallery</galleryLink>."
                                        components={{
                                            galleryLink: (
                                                <a
                                                    className={styles.galleryLink}
                                                    href="https://interacty.me/template-gallery"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={handleGalleryOpen}
                                                >
                                                    {t('full gallery')}
                                                </a>
                                            ),
                                        }}
                                    />
                                </p>
                            </div>
                            <div className={styles.recommendationGrid}>
                                {recommendations.map((recommendation, recommendationIndex) => {
                                    const { template } = recommendation

                                    return (
                                        <article key={template.id} className={styles.recommendationCard}>
                                            <h4 className={styles.templateName}>{template.name}</h4>
                                            <img
                                                className={styles.templatePreview}
                                                src={template.previewUrl}
                                                alt=""
                                                aria-hidden="true"
                                            />
                                            <div className={styles.templateActions}>
                                                <a
                                                    className={styles.templateLink}
                                                    href={template.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={() =>
                                                        handleTemplateOpen(recommendation, recommendationIndex)
                                                    }
                                                >
                                                    {t('View')}
                                                </a>
                                                <button
                                                    type="button"
                                                    className={styles.templateStart}
                                                    data-test={`event-creator-start-${template.id}`}
                                                    onClick={() =>
                                                        handleTemplateStart(recommendation, recommendationIndex)
                                                    }
                                                >
                                                    {t('Start')}
                                                </button>
                                            </div>
                                        </article>
                                    )
                                })}
                            </div>
                            <div className={styles.moreRecommendations}>
                                {recommendationStatus === 'error' && (
                                    <p className={styles.moreRecommendationsError} role="alert">
                                        {t('Could not find more templates. Please try again.')}
                                    </p>
                                )}
                                <button
                                    type="button"
                                    className={classNames(styles.submit, styles.moreRecommendationsButton)}
                                    data-test="event-creator-more-recommendations"
                                    disabled={isLoadingRecommendations || !hasMoreRecommendations}
                                    onClick={handleAlternativeRecommendations}
                                >
                                    {isLoadingRecommendations && (
                                        <span className={styles.buttonSpinner} aria-hidden="true" />
                                    )}
                                    {t(
                                        isLoadingRecommendations
                                            ? 'Finding more templates...'
                                            : hasMoreRecommendations
                                            ? 'Show other templates'
                                            : 'No more matching templates',
                                    )}
                                </button>
                            </div>
                        </section>
                    )}
                </>
            )}
            <div className={styles.collapseFooter}>
                <button
                    type="button"
                    className={styles.collapseLink}
                    data-test="event-creator-collapse"
                    onClick={handleCollapse}
                >
                    {t('Collapse')}
                </button>
            </div>
        </section>
    )
}

EventCreator.propTypes = {
    initiallyCollapsed: PropTypes.bool,
    isAuthorized: PropTypes.bool,
    experimentVariant: PropTypes.string,
    onboardingInterests: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            name: PropTypes.string,
        }),
    ),
    onStartTemplate: PropTypes.func.isRequired,
    surface: PropTypes.string,
}

EventCreator.defaultProps = {
    initiallyCollapsed: false,
    isAuthorized: false,
    experimentVariant: EVENT_CREATOR_EXPERIMENT_VARIANT,
    onboardingInterests: [],
    surface: 'my_projects',
}

export default EventCreator
